(() => {
  'use strict';

  const RUNTIME_KEY = '__u20CapsuleLoaderV610';
  if (window[RUNTIME_KEY]?.boot) {
    window[RUNTIME_KEY].boot();
    return;
  }

  const MOBILE_QUERY = '(max-width: 749px)';
  const READY_CLASS = 'u20-capsule-ready';
  const machineBoot = window.__u20MobileMachineBoot;
  const ASSET_TIMEOUT = 9000;
  const VIDEO_READY_TIMEOUT = 7000;
  const MAX_ACTIVATION_RETRIES = 2;
  const EASTER_DISSOLVE_FALLBACK_AT = 11;
  const EASTER_DISSOLVE_DURATION = 900;
  const VENDING_HANDOFF_EVENT = 'u20:vending:easter-handoff';
  let easterTakeover = null;
  const hostRecords = new Map();

  function abortError(message = 'capsule activation aborted') {
    const error = new Error(message);
    error.name = 'AbortError';
    return error;
  }

  function getRecord(host) {
    let record = hostRecords.get(host);
    if (!record) {
      record = {
        activation: null,
        controller: null,
        retryTimer: 0,
        retryCount: 0,
        app: null,
        surfaces: []
      };
      hostRecords.set(host, record);
    }
    return record;
  }

  function canEnhance() {
    if (machineBoot?.canEnhance) return machineBoot.canEnhance();
    if (!window.matchMedia(MOBILE_QUERY).matches) return false;
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection?.saveData) return false;
    if (connection?.effectiveType === 'slow-2g' || connection?.effectiveType === '2g') return false;
    if (navigator.deviceMemory && navigator.deviceMemory <= 2) return false;
    return true;
  }

  function beginPending() {
    machineBoot?.beginCapsulePending?.();
  }

  function endPending() {
    machineBoot?.endCapsulePending?.();
    document.documentElement.classList.remove('u20-capsule-pending');
  }

  function loadImage(url, timeout = ASSET_TIMEOUT, signal) {
    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(abortError());
        return;
      }
      const image = new Image();
      let timer = 0;
      const cleanup = () => {
        window.clearTimeout(timer);
        image.onload = null;
        image.onerror = null;
        signal?.removeEventListener('abort', onAbort);
      };
      const onAbort = () => {
        cleanup();
        image.removeAttribute('src');
        reject(abortError());
      };
      timer = window.setTimeout(() => {
        cleanup();
        reject(new Error('capsule image timeout'));
      }, timeout);
      image.onload = () => {
        cleanup();
        resolve(image);
      };
      image.onerror = () => {
        cleanup();
        reject(new Error('capsule image failed'));
      };
      signal?.addEventListener('abort', onAbort, { once: true });
      image.src = url;
    });
  }

  function loadStyle(root, url, timeout = ASSET_TIMEOUT, signal) {
    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(abortError());
        return;
      }
      const link = document.createElement('link');
      let timer = 0;
      const cleanup = () => {
        window.clearTimeout(timer);
        link.onload = null;
        link.onerror = null;
        signal?.removeEventListener('abort', onAbort);
      };
      const fail = message => {
        cleanup();
        link.remove();
        reject(new Error(message));
      };
      const onAbort = () => {
        cleanup();
        link.remove();
        reject(abortError());
      };
      timer = window.setTimeout(() => fail('capsule stylesheet timeout'), timeout);
      link.rel = 'stylesheet';
      link.href = url;
      link.onload = () => {
        cleanup();
        resolve(link);
      };
      link.onerror = () => fail('capsule stylesheet failed');
      signal?.addEventListener('abort', onAbort, { once: true });
      root.appendChild(link);
    });
  }

  function prepareEasterTakeover(videoUrl) {
    if (easterTakeover?.video?.isConnected) return easterTakeover;

    const vending = document.querySelector('[data-u20-vending]');
    const vendingStage = vending?.querySelector('[data-u20-vending-stage]');
    if (!vending || !vendingStage || !videoUrl) return null;

    const video = document.createElement('video');
    video.dataset.u20CapsuleEasterVideo = '';
    video.src = videoUrl;
    video.muted = true;
    video.autoplay = false;
    video.playsInline = true;
    video.preload = 'auto';
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('aria-hidden', 'true');
    Object.assign(video.style, {
      position: 'absolute',
      zIndex: '40',
      inset: '0',
      width: '100%',
      height: '100%',
      objectFit: 'fill',
      objectPosition: '50% 50%',
      transform: 'translate3d(3.2%, -0.08%, 0) scale(1.055)',
      transformOrigin: '50% 50%',
      opacity: '0',
      visibility: 'hidden',
      pointerEvents: 'auto',
      willChange: 'opacity'
    });
    vendingStage.appendChild(video);
    video.load();

    const state = {
      vending,
      video,
      started: false,
      dissolving: false,
      frameCallback: 0,
      cleanupTimer: 0,
      readyTimer: 0,
      readyCancel: null,
      scrollFrame: 0,
      scrollReleased: false,
      handoffApplied: false,
      released: false,
      release: null,
      watchPlayback: null
    };
    state.release = () => releaseEasterVideo(state);
    easterTakeover = state;
    return state;
  }

  function forceVendingElementOff(vending) {
    if (!vending) return;
    vending.dataset.u20EasterPowerOverride = 'off';
    vending.dispatchEvent(new CustomEvent(VENDING_HANDOFF_EVENT, {
      detail: { powered: false, source: 'capsule-easter' }
    }));
  }

  function forceEasterVendingOff(state) {
    if (!state || state.handoffApplied) return;
    state.handoffApplied = true;
    forceVendingElementOff(state.vending);
  }

  function releaseEasterScroll(state) {
    if (!state || state.scrollReleased) return;
    state.scrollReleased = true;
    state.video.style.pointerEvents = 'none';
    if (window.U20MobileScrollRecovery?.recover) {
      window.U20MobileScrollRecovery.recover('capsule-work-video-complete', {forceThemeUnlock: true});
    } else {
      recoverEasterScroll();
    }
  }

  function pageHasOpenScrollOwner() {
    const knownOwner = document.querySelector([
      '.drawer.is-open',
      'header-search-popdown details[open]',
      '.modal__overlay--password.is-open',
      '.product-quick-add.is-open',
      '[data-u20-mobile-drawer-cloud]:not([hidden])'
    ].join(','));
    if (knownOwner) return true;
    return Array.from(document.querySelectorAll('[role="dialog"][aria-modal="true"]'))
      .some(dialog => {
        if (dialog.hidden || dialog.getAttribute('aria-hidden') === 'true') return false;
        if (dialog.closest('[hidden], [aria-hidden="true"], details:not([open])')) return false;
        const style = window.getComputedStyle(dialog);
        return style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          style.pointerEvents !== 'none' &&
          dialog.getClientRects().length > 0;
      });
  }

  function recoverEasterScroll(attempt = 0) {
    window.requestAnimationFrame(() => {
      if (pageHasOpenScrollOwner()) return;
      const htmlLocked = document.documentElement.hasAttribute('data-scroll-locked');
      const bodyFixed = document.body.style.position === 'fixed';
      const bodyHidden = document.body.style.overflow === 'hidden';
      if (!htmlLocked && !bodyFixed && !bodyHidden) return;
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
      if (attempt < 2) window.setTimeout(() => recoverEasterScroll(attempt + 1), 40);
    });
  }

  function revealEasterVideo(state) {
    if (!state || state.video.readyState < 2) return false;
    state.video.style.visibility = 'visible';
    state.video.style.opacity = '1';
    return true;
  }

  function releaseEasterVideo(state) {
    if (!state || state.released) return;
    state.released = true;
    forceEasterVendingOff(state);
    releaseEasterScroll(state);
    window.clearTimeout(state.cleanupTimer);
    window.clearTimeout(state.readyTimer);
    if (state.scrollFrame) window.cancelAnimationFrame(state.scrollFrame);
    if (typeof state.readyCancel === 'function') state.readyCancel();
    if (state.frameCallback && typeof state.video.cancelVideoFrameCallback === 'function') {
      state.video.cancelVideoFrameCallback(state.frameCallback);
    }
    if (state.watchPlayback) state.video.removeEventListener('timeupdate', state.watchPlayback);
    if (state.release) {
      state.video.removeEventListener('ended', state.release);
      state.video.removeEventListener('error', state.release);
      window.removeEventListener('pagehide', state.release);
    }
    try { state.video.pause(); } catch (_) {}
    state.video.remove();
    if (easterTakeover === state) easterTakeover = null;
  }

  function waitForEasterVideo(state, timeout = VIDEO_READY_TIMEOUT) {
    if (!state || state.released) return Promise.reject(new Error('easter video released'));
    if (state.video.readyState >= 2) return Promise.resolve();

    return new Promise((resolve, reject) => {
      let settled = false;
      const cleanup = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(state.readyTimer);
        state.video.removeEventListener('loadeddata', ready);
        state.video.removeEventListener('canplay', ready);
        state.video.removeEventListener('error', failed);
        state.video.removeEventListener('abort', failed);
        state.readyCancel = null;
      };
      const ready = () => {
        cleanup();
        resolve();
      };
      const failed = error => {
        cleanup();
        reject(error instanceof Error ? error : new Error('easter video failed'));
      };

      state.readyCancel = () => failed(new Error('easter video released'));
      state.video.addEventListener('loadeddata', ready, { once: true });
      state.video.addEventListener('canplay', ready, { once: true });
      state.video.addEventListener('error', failed, { once: true });
      state.video.addEventListener('abort', failed, { once: true });
      state.readyTimer = window.setTimeout(() => {
        failed(new Error('easter video timeout'));
      }, timeout);
    });
  }

  function dissolveEasterVideo(state) {
    if (!state || state.dissolving || state.released) return;
    state.dissolving = true;
    state.video.style.transition = `opacity ${EASTER_DISSOLVE_DURATION}ms cubic-bezier(.4, 0, .2, 1)`;
    state.video.style.opacity = '0';
    state.cleanupTimer = window.setTimeout(
      () => releaseEasterVideo(state),
      EASTER_DISSOLVE_DURATION + 100
    );
  }

  function easterDissolveAt(video) {
    const duration = Number(video?.duration);
    if (Number.isFinite(duration) && duration > 0) {
      return Math.max(0, duration - EASTER_DISSOLVE_DURATION / 1000);
    }
    return EASTER_DISSOLVE_FALLBACK_AT;
  }

  function monitorEasterPlayback(state) {
    const check = () => {
      if (state.released || state.dissolving) return;
      if (state.video.currentTime >= easterDissolveAt(state.video)) {
        dissolveEasterVideo(state);
        return;
      }
      if (typeof state.video.requestVideoFrameCallback === 'function') {
        state.frameCallback = state.video.requestVideoFrameCallback(check);
      }
    };

    state.watchPlayback = () => {
      if (state.video.currentTime >= easterDissolveAt(state.video)) dissolveEasterVideo(state);
    };
    state.video.addEventListener('timeupdate', state.watchPlayback);
    state.video.addEventListener('ended', state.release, { once: true });
    state.video.addEventListener('error', state.release, { once: true });
    window.addEventListener('pagehide', state.release, { once: true });
    check();
  }

  function playEasterTakeover(videoUrl) {
    const state = prepareEasterTakeover(videoUrl);
    if (!state) {
      forceVendingElementOff(document.querySelector('[data-u20-vending]'));
      return;
    }
    if (state.started) return;
    state.started = true;
    forceEasterVendingOff(state);
    window.addEventListener('pagehide', state.release, { once: true });

    try { state.video.currentTime = 0; } catch (_) {}
    revealEasterVideo(state);

    const startY = window.scrollY || window.pageYOffset || 0;
    const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    const targetY = Math.max(0, Math.min(maxY, startY + state.vending.getBoundingClientRect().top));
    const distance = targetY - startY;
    const duration = 1050;
    const startedAt = performance.now();
    const playVideo = async () => {
      try {
        await waitForEasterVideo(state);
        if (state.released) return;
        revealEasterVideo(state);
        await state.video.play();
        monitorEasterPlayback(state);
      } catch (_) {
        releaseEasterVideo(state);
      }
    };

    const step = now => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const eased = progress * progress * (3 - 2 * progress);
      window.scrollTo(0, startY + distance * eased);
      if (progress < 1) {
        state.scrollFrame = window.requestAnimationFrame(step);
      } else {
        state.scrollFrame = 0;
        window.scrollTo(0, targetY);
        playVideo();
      }
    };

    state.scrollFrame = window.requestAnimationFrame(step);
  }

  function resolveSurfaces(host) {
    const start = host.closest('[data-u20-secondary-surface="start"]');
    if (!start) return [];
    const surfaces = [start];
    const end = [...document.querySelectorAll('[data-u20-secondary-surface="end"]')]
      .find(candidate => Boolean(start.compareDocumentPosition(candidate) & Node.DOCUMENT_POSITION_FOLLOWING));
    if (end) surfaces.push(end);
    return surfaces;
  }

  function updateReadyClass() {
    const hasReadyHost = [...hostRecords.entries()].some(([host]) => (
      host.isConnected && host.dataset.u20CapsuleInitialized === 'ready'
    ));
    document.body.classList.toggle(READY_CLASS, hasReadyHost);
  }

  function setOriginalSurfacesHidden(record, hidden) {
    record.surfaces.forEach(section => {
      section.classList.toggle('u20-capsule-original-hidden', hidden);
    });
  }

  function restoreFallback(host) {
    const record = getRecord(host);
    record.app?.destroy?.();
    record.app = null;
    setOriginalSurfacesHidden(record, false);
    if (host.shadowRoot) host.shadowRoot.replaceChildren();
    host.removeAttribute('data-u20-capsule-initialized');
    host.hidden = true;
    updateReadyClass();
    endPending();
  }

  function scheduleRetry(host) {
    if (!host.isConnected) return;
    const record = getRecord(host);
    const attempt = record.retryCount;
    if (attempt >= MAX_ACTIVATION_RETRIES || record.retryTimer) return;
    record.retryCount += 1;
    record.retryTimer = window.setTimeout(() => {
      record.retryTimer = 0;
      if (host.isConnected) activate(host);
    }, 1000 + attempt * 2000);
  }

  function activate(host) {
    if (!host?.isConnected || host.dataset.u20CapsuleInitialized === 'ready') {
      return Promise.resolve();
    }
    if (!canEnhance()) {
      endPending();
      return Promise.resolve();
    }
    const record = getRecord(host);
    if (record.activation) return record.activation;
    beginPending();
    record.surfaces = resolveSurfaces(host);
    const controller = new AbortController();
    const { signal } = controller;
    record.controller = controller;

    const activation = (async () => {
      host.dataset.u20CapsuleInitialized = 'loading';

      const template = host.querySelector('template');
      const configNode = host.querySelector('[data-u20-capsule-config]');
      if (!template || !configNode || typeof window.U20CapsuleApp !== 'function') {
        restoreFallback(host);
        scheduleRetry(host);
        return;
      }

      try {
        const config = JSON.parse(configNode.textContent);
        const root = host.shadowRoot || host.attachShadow({ mode: 'open' });
        root.replaceChildren();
        await Promise.all([
          loadStyle(root, config.assets.stylesheet, ASSET_TIMEOUT, signal),
          loadImage(config.assets.machineOff, ASSET_TIMEOUT, signal)
        ]);
        if (signal.aborted || !host.isConnected) throw abortError();

        root.appendChild(template.content.cloneNode(true));
        config.onEasterPrepare = () => prepareEasterTakeover(config.assets.easterVideo);
        config.onEasterComplete = () => playEasterTakeover(config.assets.easterVideo);
        record.app = window.U20CapsuleApp(root, config) || null;
        if (signal.aborted || !host.isConnected) throw abortError();

        host.dataset.u20CapsuleInitialized = 'ready';
        host.hidden = false;
        setOriginalSurfacesHidden(record, true);
        record.retryCount = 0;
        updateReadyClass();
        endPending();
      } catch (error) {
        if (signal.aborted || !host.isConnected || error?.name === 'AbortError') {
          destroyHost(host);
          return;
        }
        console.warn('[UnDeR20 capsule] fallback retained:', error);
        restoreFallback(host);
        scheduleRetry(host);
      }
    })().finally(() => {
      if (record.activation === activation) record.activation = null;
      if (record.controller === controller) record.controller = null;
    });

    record.activation = activation;
    return activation;
  }

  function destroyHost(host) {
    const record = hostRecords.get(host);
    if (!record) return;
    record.controller?.abort();
    record.controller = null;
    if (record.retryTimer) window.clearTimeout(record.retryTimer);
    record.retryTimer = 0;
    record.app?.destroy?.();
    record.app = null;
    setOriginalSurfacesHidden(record, false);
    if (easterTakeover) releaseEasterVideo(easterTakeover);
    if (host.shadowRoot) host.shadowRoot.replaceChildren();
    host.removeAttribute('data-u20-capsule-initialized');
    host.hidden = true;
    hostRecords.delete(host);
    updateReadyClass();
    endPending();
  }

  function findHosts(scope = document) {
    const hosts = [];
    if (scope.matches?.('[data-u20-capsule-host]')) hosts.push(scope);
    scope.querySelectorAll?.('[data-u20-capsule-host]').forEach(host => hosts.push(host));
    return hosts;
  }

  function boot(scope = document) {
    findHosts(scope).forEach(activate);
  }

  function retryAfterResume() {
    findHosts().forEach(host => {
      if (host.dataset.u20CapsuleInitialized !== 'ready') {
        const record = getRecord(host);
        if (record.retryTimer) window.clearTimeout(record.retryTimer);
        record.retryTimer = 0;
        record.retryCount = 0;
      }
    });
    boot();
  }

  function onSectionLoad(event) {
    boot(event.target);
  }

  function onSectionUnload(event) {
    if (!event.target) return;
    [...hostRecords.keys()].forEach(host => {
      if (event.target.contains(host)) destroyHost(host);
    });
  }

  function onPageShow(event) {
    if (!event.persisted) return;
    retryAfterResume();
    window.requestAnimationFrame(() => {
      window.U20MobileScrollRecovery?.recover?.('capsule-loader-bfcache-return', {forceThemeUnlock: true});
    });
  }

  function destroy() {
    window.removeEventListener('online', retryAfterResume);
    window.removeEventListener('pageshow', onPageShow);
    document.removeEventListener('shopify:section:load', onSectionLoad);
    document.removeEventListener('shopify:section:unload', onSectionUnload);
    [...hostRecords.keys()].forEach(destroyHost);
    if (window[RUNTIME_KEY]?.destroy === destroy) delete window[RUNTIME_KEY];
  }

  window[RUNTIME_KEY] = { boot, destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  } else {
    boot();
  }
  window.addEventListener('online', retryAfterResume);
  window.addEventListener('pageshow', onPageShow);
  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:unload', onSectionUnload);
})();
